import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../hooks/useAuth";
import { useNotification } from "../../contexts/NotificationContext";
import Loading from "../common/Loading";
import Button from "../common/Button";

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { notify } = useNotification();
  const [project, setProject] = useState(null);
  const [issues, setIssues] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const projectDoc = await getDoc(doc(db, "projects", projectId));

        if (!projectDoc.exists()) {
          notify.error("Project not found");
          navigate("/projects");
          return;
        }

        const projectData = {
          id: projectDoc.id,
          ...projectDoc.data(),
        };

        // Manual permission check
        const userIsMember = projectData.members?.includes(userProfile?.uid);
        const userIsAdmin = userProfile?.role === "admin";
        const userIsManager = userProfile?.role === "manager";

        if (!userIsMember && !userIsAdmin && !userIsManager) {
          notify.error("You do not have access to this project");
          navigate("/projects");
          return;
        }

        setProject(projectData);

        // Fetch project issues
        const issuesQuery = query(
          collection(db, "issues"),
          where("projectId", "==", projectId)
        );
        const issuesSnapshot = await getDocs(issuesQuery);
        const issuesData = issuesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setIssues(issuesData);

        // Fetch project members
        if (projectData.members && projectData.members.length > 0) {
          const membersQuery = query(
            collection(db, "users"),
            where("uid", "in", projectData.members)
          );
          const membersSnapshot = await getDocs(membersQuery);
          const membersData = membersSnapshot.docs.map((doc) => doc.data());
          setMembers(membersData);
        }
      } catch (error) {
        console.error("Error fetching project:", error);
        if (error.code === "permission-denied") {
          notify.error("You do not have permission to view this project");
        } else {
          notify.error("Failed to load project");
        }
        navigate("/projects");
      } finally {
        setLoading(false);
      }
    };

    if (userProfile && projectId) {
      fetchProjectData();
    }
  }, [projectId, navigate, notify, userProfile]);

  if (loading) {
    return <Loading text="Loading project..." />;
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Project not found or no access
        </h2>
        <Button onClick={() => navigate("/projects")} className="mt-4">
          Back to Projects
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate("/projects")}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2"
          >
            ← Back to Projects
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {project.emoji} {project.name} ({project.key})
          </h1>
        </div>
        <Button
          onClick={() => navigate("/issues?create=true&project=" + projectId)}
          variant="primary"
        >
          Create Issue
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Description */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Description
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              {project.description || "No description provided."}
            </p>
          </div>

          {/* Issues List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Issues
              </h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {issues.length} issue{issues.length !== 1 ? "s" : ""}
              </span>
            </div>

            {issues.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 dark:text-gray-500 text-4xl mb-2">
                  📋
                </div>
                <p className="text-gray-500 dark:text-gray-400">
                  No issues yet
                </p>
                <Button
                  onClick={() =>
                    navigate("/issues?create=true&project=" + projectId)
                  }
                  variant="primary"
                  className="mt-4"
                >
                  Create First Issue
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {issues.map((issue) => (
                  <div
                    key={issue.id}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {issue.issueKey}: {issue.title}
                      </h3>
                      <div className="flex items-center space-x-3 mt-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            issue.status === "todo"
                              ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                              : issue.status === "in-progress"
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                              : issue.status === "in-review"
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                              : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                          }`}
                        >
                          {issue.status.replace("-", " ")}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                          {issue.type}
                        </span>
                        <span
                          className={`text-sm px-2 py-1 rounded ${
                            issue.priority === "low"
                              ? "bg-green-100 text-green-800"
                              : issue.priority === "medium"
                              ? "bg-yellow-100 text-yellow-800"
                              : issue.priority === "high"
                              ? "bg-orange-100 text-orange-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {issue.priority}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => navigate(`/issues/${issue.id}`)}
                    >
                      View
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Project Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Project Stats
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Total Issues:
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {issues.length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Team Members:
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {members.length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Created:
                </span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {new Date(project.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Last Updated:
                </span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {new Date(
                    project.updatedAt || project.createdAt
                  ).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Team Members
            </h2>
            <div className="space-y-3">
              {members.map((member) => (
                <div key={member.uid} className="flex items-center space-x-3">
                  {member.avatarUrl ? (
                    <img
                      src={member.avatarUrl}
                      alt={member.displayName}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {member.displayName?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {member.displayName}
                      {member.uid === userProfile?.uid && (
                        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                          (You)
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {member.role}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Quick Actions
            </h2>
            <div className="space-y-2">
              <Button
                onClick={() =>
                  navigate("/issues?create=true&project=" + projectId)
                }
                variant="primary"
                className="w-full"
              >
                Create Issue
              </Button>
              {(userProfile?.role === "admin" ||
                userProfile?.role === "manager") && (
                <Button
                  onClick={() => navigate(`/projects/${projectId}/settings`)}
                  variant="outline"
                  className="w-full"
                >
                  Project Settings
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
